import { useCallback, useRef, useState } from "react";
import { inspectionsAPI } from "../../../../../services/api";

export const useInspectionBundle = (projectId) => {
  const [openId, setOpenId] = useState(null);
  const [tab, setTab] = useState("summary");

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [checklist, setChecklist] = useState(null);
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [checklistForbidden, setChecklistForbidden] = useState(false);

  const [findings, setFindings] = useState([]);
  const [findingsLoading, setFindingsLoading] = useState(false);
  const [findingsForbidden, setFindingsForbidden] = useState(false);

  const reqRef = useRef(0);

  const close = useCallback(() => {
    setOpenId(null);
    setDetail(null);
    setChecklist(null);
    setFindings([]);
    setTab("summary");
  }, []);

  const open = useCallback(
    async (inspectionId, initialTab = "summary") => {
      if (!projectId || !inspectionId) return;

      const reqId = ++reqRef.current;
      setOpenId(inspectionId);
      setTab(initialTab || "summary");

      // ---- 1) Detail
      setDetailLoading(true);
      let inspectionTypeName = null;
      let inspectionTypeIds = null;

      try {
        const res = await inspectionsAPI.getOne(projectId, inspectionId);
        if (reqRef.current !== reqId) return;

        setDetail(res.data);
        inspectionTypeName = res.data?.inspection_type || null;
        inspectionTypeIds = res.data?.inspection_type_ids || null;
      } finally {
        if (reqRef.current === reqId) setDetailLoading(false);
      }

      // ---- 2) Checklist + Findings
      setChecklistLoading(true);
      setFindingsLoading(true);

      try {
        const [cRes, fRes] = await Promise.allSettled([
          inspectionsAPI.getChecklist(projectId, inspectionId),
          inspectionsAPI.listFindings(projectId, inspectionId),
        ]);

        if (reqRef.current !== reqId) return;

        // Checklist
        if (cRes.status === "fulfilled") {
          let c = cRes.value.data;
          setChecklistForbidden(false);

          const itemsCount = c?.items?.length ?? 0;
          const isSeeded = itemsCount > 0;

          // 🔹 Auto-seed from project inspection templates
          if (!isSeeded) {
            try {
              // 1) Load all project inspection types
              const typesRes =
                await inspectionsAPI.listProjectInspectionTypes(projectId);
              const types = typesRes.data || [];

              // 2) Determine which types to use:
              //    - Prefer explicit inspection_type_ids (list of project_inspection_types.id)
              //    - Fallback to legacy inspection_type name (string)
              let selectedTypes = [];

              if (
                Array.isArray(inspectionTypeIds) &&
                inspectionTypeIds.length > 0
              ) {
                selectedTypes = types.filter((t) =>
                  inspectionTypeIds.includes(t.id),
                );
              } else if (inspectionTypeName) {
                const matchedType = types.find(
                  (t) => (t.name || "").trim() === inspectionTypeName.trim(),
                );
                if (matchedType) selectedTypes = [matchedType];
              }

              if (selectedTypes.length > 0) {
                const seedItems = [];

                // 3) For each selected type, load its checklist template items
                for (const t of selectedTypes) {
                  const tplRes =
                    await inspectionsAPI.listProjectInspectionChecklistItems(
                      projectId,
                      t.id,
                    );
                  const tplItems = tplRes.data || [];

                  tplItems.forEach((it) => {
                    // User-friendly: bake the inspection type name into the category
                    // so the UI clearly shows which checklist items belong to which type.
                    const baseCat = it.item_category?.trim();
                    const catLabel = baseCat
                      ? `${t.name} – ${baseCat}`
                      : t.name;

                    seedItems.push({
                      item_category: catLabel,
                      item_text: it.item_text,
                      inspection_note: it.inspection_note || null,
                      is_mandatory: it.is_required !== false,
                      source_template_item_id: it.id,
                      requirement_ids: Array.isArray(it.requirement_ids)
                        ? it.requirement_ids
                        : [],
                    });
                  });
                }

                if (seedItems.length > 0) {
                  // 4) Seed the inspection checklist using the merged template items
                  await inspectionsAPI.seedChecklist(projectId, inspectionId, {
                    template_id: null, // multi-source templates
                    items: seedItems,
                  });

                  if (reqRef.current !== reqId) return;

                  const c2 = await inspectionsAPI.getChecklist(
                    projectId,
                    inspectionId,
                  );
                  if (reqRef.current !== reqId) return;
                  c = c2.data;
                }
              }
            } catch (e) {
              // If project templates aren’t configured, we just leave the checklist empty.
              console.error(
                "Failed to seed checklist from project inspection templates",
                e,
              );
            }
          }

          setChecklist(c);
        } else {
          setChecklist(null);
          setChecklistForbidden(cRes.reason?.response?.status === 403);
        }

        // Findings
        if (fRes.status === "fulfilled") {
          setFindings(fRes.value.data || []);
          setFindingsForbidden(false);
        } else {
          setFindings([]);
          setFindingsForbidden(fRes.reason?.response?.status === 403);
        }
      } finally {
        if (reqRef.current === reqId) {
          setChecklistLoading(false);
          setFindingsLoading(false);
        }
      }
    },
    [projectId],
  );

  return {
    openId,
    tab,
    setTab,
    detail,
    setDetail,
    detailLoading,
    checklist,
    setChecklist,
    checklistLoading,
    checklistForbidden,
    findings,
    setFindings,
    findingsLoading,
    findingsForbidden,
    open,
    close,
  };
};
